/**
 * Listing-status policy for the UI.
 *
 * Mirrors backend/app/services/listing_visibility.py — the backend enforces
 * these gates (409 `listing_not_tourable` / `listing_not_offerable`); the UI
 * uses them to avoid offering a CTA that is guaranteed to fail.
 *
 *   status     buyer-visible  tourable  offerable
 *   draft      no             no        no
 *   active     yes            yes       yes
 *   pending    yes            yes       yes   (backup offers / showings)
 *   sold       yes            no        no    (viewable as a comp / record)
 *   withdrawn  no             no        no
 */

export const LISTING_STATUS_SOLD = 'sold';

export const TOURABLE_LISTING_STATUSES = ['active', 'pending'];
export const OFFERABLE_LISTING_STATUSES = ['active', 'pending'];

/**
 * Normalize a raw `listing_status`; blank/legacy values read as `draft`,
 * matching the backend's `normalize_listing_status`.
 */
export const normalizeListingStatus = (status) =>
  String(status ?? '').trim().toLowerCase() || 'draft';

export const isTourable = (status) =>
  TOURABLE_LISTING_STATUSES.includes(normalizeListingStatus(status));

export const isOfferable = (status) =>
  OFFERABLE_LISTING_STATUSES.includes(normalizeListingStatus(status));

export const isSold = (status) =>
  normalizeListingStatus(status) === LISTING_STATUS_SOLD;

/**
 * One-line sale summary for a sold listing, from the `sold_price` /
 * `sold_date` the backend stamps at closing. Either may be missing (a listing
 * marked sold by hand has neither), so every combination degrades cleanly.
 *
 * @param {{sold_price?: number|null, sold_date?: string|null}} house
 * @returns {string} e.g. "Sold for $440,000 on Sep 27, 2026", or "Sold".
 */
export const describeSale = (house) => {
  const parts = ['Sold'];
  if (house?.sold_price != null) {
    parts.push(`for $${Number(house.sold_price).toLocaleString()}`);
  }
  // `sold_date` is a calendar date ("YYYY-MM-DD"). Build it in local time —
  // `new Date("2026-09-27")` is UTC midnight and renders as the 26th in the US.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(house?.sold_date || '');
  if (match) {
    const soldOn = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    parts.push(
      `on ${soldOn.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    );
  }
  return parts.join(' ');
};
